import { useDispatch } from 'react-redux';
import i18n from 'i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { setAppLoader } from '@/redux/common/CommonActions';
import { NOTIFICATION_TYPES, showNotification } from '@/views/components/Modals/notification';

import { saveClaim } from '../data-provider/save-claim';
import type { SaveClaimData } from '../data-provider/save-claim/api';

export interface UseSaveClaimOptions {
  onSuccess?: (claimKey: number) => void;
  onError?: (error: Error) => void;
  invalidateQueries?: boolean;
}

export const useSaveClaim = (options?: UseSaveClaimOptions) => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: (data: SaveClaimData) => saveClaim(data),
    onError: (error: Error, variables) => {
      dispatch(setAppLoader(false));
      showNotification({
        message: variables?.claimKey === 0 ? 'Failed to create claim.' : 'Failed to update claim data.',
        type: NOTIFICATION_TYPES.ERROR,
      });
      options?.onError?.(error);
    },
    onMutate: () => {
      dispatch(setAppLoader(true));
    },
    onSettled: () => {
      dispatch(setAppLoader(false));
    },
    onSuccess: (claimKey, variables) => {
      dispatch(setAppLoader(false));

      if (options?.invalidateQueries !== false) {
        // Invalidate related queries
        queryClient.invalidateQueries({ queryKey: ['claims-loss-and-damage-list'] });
        queryClient.invalidateQueries({ queryKey: ['claims-loss-and-damage-details', variables.claimKey] });
        queryClient.invalidateQueries({ queryKey: ['claims-loss-and-damage-notes', variables.claimKey] });
        queryClient.invalidateQueries({ queryKey: ['claims-loss-and-damage-settlements', variables.claimKey] });
        queryClient.invalidateQueries({ queryKey: ['claims-loss-and-damage-items', variables.claimKey] });
        queryClient.invalidateQueries({ queryKey: ['claims-loss-and-damage-files', variables.claimKey] });
      }

      showNotification({
        message:
          variables.claimKey === 0
            ? `${i18n.t('translation:claims.successFullyCreated')}. ${i18n.t('translation:default.fields.claimNumber')}: ${claimKey}`
            : 'Successfully updated claim data.',
        type: NOTIFICATION_TYPES.SUCCESS,
      });

      options?.onSuccess?.(claimKey);
    },
  });
};
